import {
	BulkCreateSecuritiesDto,
	BulkOperationResult,
	BulkSoftDeleteSecuritiesDto,
	BulkUpdateSecuritiesDto,
	CreateSecurityDto,
	CreateUpdateSecurityInterestDefinitionDto,
	CreateUpdateSecurityRedemptionRightsDto,
	DataSecurityInterestDefinitionTct,
	DataSecurityRedemptionRightsTct,
	DataSecurityTct,
	GetPersonSecuritiesRes,
	GetSecurityDataForMipRes,
	GetSecurityListFilterDto,
	IsSecuritiesUsedInAnyMipRes,
	UpdateSecurityAuthorisedCapitalDto,
	UpdateSecurityDto,
	DownloadSecuritiesCsvDto,
	CreateUpdateSecurityCatchUpRightsDto,
	DataSecurityCatchUpRightsTct,
	DataSecurityWithIssuerTct,
	BulkUpdateTrackedSecuritiesDto,
	DataSecurityExchangeRightsTct,
	CreateUpdateSecurityExchangeRightsDto,
	UpdateCurrencyForPersonSecurities,
	UpdateSecurityParticipationThresholdDto,
	GetPersonSecuritiesWithFeaturesAndRanksRes,
	SecurityRankRes,
	UpsertSecurityRankDto,
	SecurityRankDefinitionRes,
	CreateSecurityRankDefinitionDto,
	UpdateSecurityRankDefinitionDto,
	UpsertSecurityIncomeRanksDto,
	DataSecurityWithAllFeaturesAndRanksTct,
	CreateUpdateSecurityLiquidationPreferenceDto,
	SecurityLiquidationPreferenceRes,
} from '@dealstack/shared/lib/generated';
import type {
	SecurityRatchetScheduleRes,
	CreateUpdateSecurityRatchetScheduleBody,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/data.mock.ts';
import { ServiceRequestOptions } from '@dealstack/frontend/shared/other/react/ApiWrapper/types.ts';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer.ts';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';

type GetPersonSecuritiesArgs = {
	withHoldingTotals?: boolean;
	body?: GetSecurityListFilterDto;
	filterDate?: string;
};

export const getSecurityDetails = async (securityId: string, date?: string) => {
	return makeRequest<ApiV1.Response<DataSecurityWithAllFeaturesAndRanksTct>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_SECURITYID_GET, { securityId }),
		method: HTTP_METHOD.GET,
		queryString: date ? { date } : undefined,
	}).then((response) => response.payload);
};

export const getSecuritiesDataForMip = async (
	endpoint: string,
	args: { issuerId?: string } | undefined,
	signal?: AbortSignal
) => {
	return makeRequest<ApiV1.Response<GetSecurityDataForMipRes[]>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
			queryString: { issuerId: args?.issuerId },
		},
		{ abortSignal: signal }
	).then((r) => r.payload);
};

export const getPersonSecurities = async (
	endpoint: string,
	args: GetPersonSecuritiesArgs | undefined,
	signal?: AbortSignal
) => {
	const withHoldingTotals = !!args?.withHoldingTotals;
	const filterDate = args?.filterDate;

	return makeRequest<ApiV1.Response<GetPersonSecuritiesRes>>(
		{
			endpoint,
			method: HTTP_METHOD.POST,
			...(args?.body ? { body: JSON.stringify(args?.body) } : {}),
			queryString: {
				...(withHoldingTotals ? { withHoldingTotals } : {}),
				...(filterDate ? { date: filterDate } : {}),
			},
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

export const getPersonSecuritiesWithFeaturesAndRanks = async (
	selectedEntityId: string,
	splitIds?: string[]
) => {
	const endpoint = formatUnicorn(API_PATHS.SECURITY_SECURITY_RANKS_PERSONID_GET, {
		personId: selectedEntityId,
	});

	return makeRequest<ApiV1.Response<GetPersonSecuritiesWithFeaturesAndRanksRes[]>>({
		endpoint,
		method: HTTP_METHOD.GET,
		queryString: splitIds ? { splitIds } : undefined,
	}).then((response) => response.payload);
};

export const getPersonSecurityDefinitionRanks = async (selectedEntityId: string) => {
	const endpoint = formatUnicorn(API_PATHS.SECURITYRANKDEFINITION_PERSONID_GET, {
		personId: selectedEntityId,
	});

	return makeRequest<ApiV1.Response<SecurityRankDefinitionRes[]>>({
		endpoint,
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const createSecurityRankDefinition = async (args: CreateSecurityRankDefinitionDto) => {
	return makeRequest<ApiV1.Response<SecurityRankDefinitionRes>>({
		endpoint: API_PATHS.SECURITYRANKDEFINITION_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((response) => response.payload);
};

export const updateSecurityRankDefinition = async (
	args: { rankId: string } & UpdateSecurityRankDefinitionDto
) => {
	const { rankId, ...rest } = args;
	const endpoint = formatUnicorn(API_PATHS.SECURITYRANKDEFINITION_RANKID_PUT, {
		rankId,
	});

	return makeRequest<ApiV1.Response<SecurityRankDefinitionRes>>({
		endpoint,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(rest),
	}).then((response) => response.payload);
};

export const deleteRank = async (rankId: string) => {
	const endpoint = formatUnicorn(API_PATHS.SECURITYRANKDEFINITION_RANKID_DELETE, {
		rankId,
	});

	return makeRequest<ApiV1.Response<SecurityRankDefinitionRes>>({
		endpoint,
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};

export const upsertSecurityRank = async (args: UpsertSecurityRankDto) => {
	return makeRequest<ApiV1.Response<SecurityRankRes>>({
		endpoint: API_PATHS.SECURITY_RANK_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((response) => response.payload);
};

export const upsertSecurityIncomeRanks = async (args: UpsertSecurityIncomeRanksDto) => {
	return makeRequest<ApiV1.Response<SecurityRankRes[]>>({
		endpoint: API_PATHS.SECURITY_RANK_INCOME_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((response) => response.payload);
};

export const deleteSecurityRank = async (id: string) => {
	const endpoint = formatUnicorn(API_PATHS.SECURITY_RANK_ID_DELETE, {
		id,
	});

	return makeRequest<ApiV1.Response<void>>({
		endpoint,
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};

export const deleteSecurityIncomeRanks = async (securityId: string) => {
	const endpoint = formatUnicorn(API_PATHS.SECURITY_RANK_INCOME_SECURITYID_DELETE, {
		securityId,
	});

	return makeRequest<ApiV1.Response<void>>({
		endpoint,
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};

export const isSecuritiesUsedInAnyMip = async (securitiesIds: string[]) => {
	return makeRequest<ApiV1.Response<IsSecuritiesUsedInAnyMipRes>>({
		endpoint: API_PATHS.SECURITY_IS_SECURITIES_USED_IN_ANY_MIP_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(securitiesIds),
	}).then((response) => response.payload);
};

export const createSecurity = async (createSecurityDto: CreateSecurityDto) => {
	return makeRequest<ApiV1.Response<DataSecurityTct>>({
		endpoint: API_PATHS.SECURITY_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(createSecurityDto),
	}).then((response) => response.payload);
};

export const updateCurrencyForPersonSecurities = async (params: {
	personId: string;
	dto: UpdateCurrencyForPersonSecurities;
}) => {
	const { personId, dto } = params;
	const endpoint = formatUnicorn(API_PATHS.SECURITY_PERSONID_CURRENCY_PUT, {
		personId,
	});

	return makeRequest<ApiV1.Response<BulkOperationResult>>({
		endpoint,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(dto),
	}).then((response) => response.payload);
};

export const bulkCreateSecurities = async (
	args: BulkCreateSecuritiesDto,
	options?: ServiceRequestOptions
) => {
	return makeRequest<ApiV1.Response<BulkOperationResult>>(
		{
			endpoint: API_PATHS.SECURITY_BULK_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ ...options }
	).then((response) => response.payload);
};

export const updateSecurity = async (body: UpdateSecurityDto) => {
	return makeRequest<ApiV1.Response<DataSecurityTct>>({
		endpoint: API_PATHS.SECURITY_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const bulkUpdateSecurities = async (
	args: BulkUpdateSecuritiesDto,
	options?: ServiceRequestOptions
) => {
	return makeRequest<ApiV1.Response<BulkOperationResult>>(
		{
			endpoint: API_PATHS.SECURITY_BULK_PUT,
			method: HTTP_METHOD.PUT,
			body: JSON.stringify(args),
		},
		{ ...options }
	).then((response) => response.payload);
};

export const softDeleteSecurity = async (securityId: string) => {
	const endpoint = formatUnicorn(API_PATHS.SECURITY_SOFT_DELETE_ONE_SECURITYID_DELETE, {
		securityId,
	});
	return makeRequest({
		endpoint,
		method: HTTP_METHOD.DELETE,
	});
};

export const bulkSoftDeleteSecurities = async (
	softDeleteSecurityDto: BulkSoftDeleteSecuritiesDto
) => {
	return makeRequest<ApiV1.Response<BulkOperationResult>>({
		endpoint: API_PATHS.SECURITY_BULK_SOFT_DELETE_DELETE,
		method: HTTP_METHOD.DELETE,
		body: JSON.stringify(softDeleteSecurityDto),
	}).then((r) => r.payload);
};

export const downloadSecuritiesExcel = async (payload: DownloadSecuritiesCsvDto): Promise<Blob> => {
	return makeRequest<Blob>({
		endpoint: API_PATHS.SECURITY_DOWNLOAD_EXCEL_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(payload),
	});
};

export const updateSecurityInterestDefinition = async ({
	securityId,
	body,
}: {
	securityId: string;
	body: CreateUpdateSecurityInterestDefinitionDto;
}) => {
	return makeRequest<ApiV1.Response<DataSecurityInterestDefinitionTct>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_SECURITYID_INTEREST_DEFINITION_POST, {
			securityId,
		}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const deleteSecurityInterestDefinition = async (securityId: string) => {
	return makeRequest<ApiV1.Response<DataSecurityInterestDefinitionTct>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_SECURITYID_INTEREST_DEFINITION_DELETE, {
			securityId,
		}),
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};

export const updateSecurityRedemptionRights = async ({
	securityId,
	body,
}: {
	securityId: string;
	body: CreateUpdateSecurityRedemptionRightsDto;
}) => {
	return makeRequest<ApiV1.Response<DataSecurityRedemptionRightsTct>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_SECURITYID_REDEMPTION_RIGHTS_POST, {
			securityId,
		}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const deleteSecurityRedemptionRights = async (securityId: string) => {
	return makeRequest<ApiV1.Response<DataSecurityInterestDefinitionTct>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_SECURITYID_REDEMPTION_RIGHTS_DELETE, {
			securityId,
		}),
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};

export const updateSecurityAuthorisedCapital = async ({
	securityId,
	body,
}: {
	securityId: string;
	body: UpdateSecurityAuthorisedCapitalDto;
}) => {
	return makeRequest<ApiV1.Response<UpdateSecurityDto>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_SECURITYID_AUTHORISED_CAPITAL_PUT, {
			securityId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const updateSecurityParticipationThreshold = async ({
	securityId,
	body,
}: {
	securityId: string;
	body: UpdateSecurityParticipationThresholdDto;
}) => {
	return makeRequest<ApiV1.Response<DataSecurityTct>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_SECURITYID_PARTICIPATION_THRESHOLD_PUT, {
			securityId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const updateSecurityCatchUpRights = async ({
	securityId,
	body,
}: {
	securityId: string;
	body: CreateUpdateSecurityCatchUpRightsDto;
}) => {
	return makeRequest<ApiV1.Response<DataSecurityCatchUpRightsTct>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_SECURITYID_CATCH_UP_RIGHTS_POST, {
			securityId,
		}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const deleteSecurityCatchUpRights = async (securityId: string) => {
	return makeRequest<ApiV1.Response<DataSecurityCatchUpRightsTct>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_SECURITYID_CATCH_UP_RIGHTS_DELETE, {
			securityId,
		}),
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};

export const getSecurityLiquidationPreference = async (securityId: string) => {
	return makeRequest<ApiV1.Response<SecurityLiquidationPreferenceRes | null>>({
		endpoint: formatUnicorn(API_PATHS.SECURITIES_SECURITYID_LIQUIDATION_PREFERENCE_GET, {
			securityId,
		}),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const updateSecurityLiquidationPreference = async ({
	securityId,
	body,
}: {
	securityId: string;
	body: CreateUpdateSecurityLiquidationPreferenceDto;
}) => {
	return makeRequest<ApiV1.Response<SecurityLiquidationPreferenceRes>>({
		endpoint: formatUnicorn(API_PATHS.SECURITIES_SECURITYID_LIQUIDATION_PREFERENCE_POST, {
			securityId,
		}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const deleteSecurityLiquidationPreference = async (securityId: string) => {
	return makeRequest<ApiV1.Response<SecurityLiquidationPreferenceRes>>({
		endpoint: formatUnicorn(API_PATHS.SECURITIES_SECURITYID_LIQUIDATION_PREFERENCE_DELETE, {
			securityId,
		}),
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};

export const getDealSecurities = async (endpoint: string) => {
	return makeRequest<ApiV1.Response<DataSecurityWithIssuerTct[]>>({
		endpoint,
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const updateTrackedSecurities = async ({
	body,
}: {
	body: BulkUpdateTrackedSecuritiesDto;
}) => {
	return makeRequest<ApiV1.Response<BulkOperationResult>>({
		endpoint: API_PATHS.SECURITY_TRACKING_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const deleteTrackedSecurities = async (params: {
	securityId: string;
	trackingSecurityIssuerId: string;
}) => {
	const { securityId, trackingSecurityIssuerId } = params;

	return makeRequest<ApiV1.Response<BulkOperationResult>>({
		endpoint: formatUnicorn(
			API_PATHS.SECURITY_SECURITYID_TRACKING_TRACKINGSECURITYISSUERID_DELETE,
			{
				securityId,
				trackingSecurityIssuerId,
			}
		),
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};

export const updateSecurityExchangeRights = async ({
	securityId,
	body,
}: {
	securityId: string;
	body: CreateUpdateSecurityExchangeRightsDto;
}) => {
	return makeRequest<ApiV1.Response<DataSecurityExchangeRightsTct>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_SECURITYID_EXCHANGE_RIGHTS_POST, {
			securityId,
		}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const deleteSecurityExchangeRights = async (securityId: string) => {
	return makeRequest<ApiV1.Response<DataSecurityExchangeRightsTct>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_SECURITYID_EXCHANGE_RIGHTS_DELETE, {
			securityId,
		}),
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};

export const getSecurityRatcheting = async (securityId: string) => {
	return makeRequest<ApiV1.Response<SecurityRatchetScheduleRes | null>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_RATCHET_SECURITYID_GET, { securityId }),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const updateSecurityRatcheting = async ({
	securityId,
	body,
}: {
	securityId: string;
	body: CreateUpdateSecurityRatchetScheduleBody;
}) => {
	return makeRequest<ApiV1.Response<SecurityRatchetScheduleRes>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_RATCHET_SECURITYID_POST, { securityId }),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const deleteSecurityRatcheting = async (securityId: string) => {
	return makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_RATCHET_SECURITYID_DELETE, { securityId }),
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};
